import React, { useEffect, useRef, useState } from 'react'
import { useResultsStore } from '../../store/ResultsStore'
import styled from 'styled-components'
import { Overlay } from 'react-bootstrap'
import { Box, Grow } from '@mui/material'
import { useClickOutsideDetector } from 'common'
import { I18n } from 'common'
import { zIndex } from '../../../../../src/theme/zIndex'
import { getSortOptions } from '../../utils/GetSortOptions'
import { SortOrderItem } from '../../../../../src/store/models/SearchModels'
import { ReactSelect } from '../../../../../src/ui-components/forms/ReactSelect'

interface SortDropdownProps {
  show: boolean
  parentRef: React.RefObject<HTMLElement>
  setShow: (show: boolean) => void
}
interface Option {
  label: string
  value: string
}

export const SortDropdown: React.FC<SortDropdownProps> = ({
  show,
  parentRef,
  setShow,
}) => {
  const resultsStore = useResultsStore()
  const options = getSortOptions()
  const { setSortOrder, sortOrder } = resultsStore

  const overlayRef = useRef(null)
  const sortByRef = useRef(null)
  const thenByRef = useRef(null)
  const [selectedSortByOption, setSelectedSortByOption] = useState(null)
  const [selectedThenByOption, setSelectedThenByOption] = useState(null)

  useClickOutsideDetector(
    [overlayRef, sortByRef, thenByRef, parentRef],
    show,
    () => setShow(false)
  )

  useEffect(() => {
    if (sortOrder?.length > 0) {
      if (!selectedSortByOption) {
        const label = options.find(
          (option) =>
            option.property === sortOrder[0].property &&
            option.dir === sortOrder[0].dir
        ).label
        sortOrder[0].label = label
        setSelectedSortByOption(sortOrder[0])
      }
      if (sortOrder.length > 1 && !selectedThenByOption) {
        const label = options.find(
          (option) =>
            option.property === sortOrder[1].property &&
            option.dir === sortOrder[1].dir
        ).label
        sortOrder[1].label = label
        setSelectedThenByOption(sortOrder[1])
      }
    }
  }, [options, selectedSortByOption, selectedThenByOption, sortOrder])

  const onChangeSortBy = (value): void => {
    setSelectedSortByOption(value)
    const sortPartial = []
    const sortOption = { ...value }
    sortPartial.push(sortOption)
    if (selectedThenByOption) {
      const thenOption = { ...selectedThenByOption }
      sortPartial.push(thenOption)
    }
    setSortOrder(sortPartial)
    setShow(false)
  }
  const onChangeThenBy = (value): void => {
    setSelectedThenByOption(value)
    const sortPartial = []
    const thenOption = { ...value }
    if (selectedSortByOption) {
      const sortOption = { ...selectedSortByOption }
      sortPartial.push(sortOption)
    }
    sortPartial.push(thenOption)
    setSortOrder(sortPartial)
    setShow(false)
  }
  const getSortByOptions = (): SortOrderItem[] => {
    if (selectedThenByOption) {
      const data = options.filter(
        (option) =>
          !(
            selectedThenByOption &&
            option.dir === selectedThenByOption.dir &&
            option.property === selectedThenByOption.property
          )
      )
      return data
    }
    return []
  }

  const getThenByOptions = (): SortOrderItem[] => {
    if (selectedSortByOption) {
      const data = options.filter(
        (option) =>
          !(
            selectedSortByOption &&
            option.dir === selectedSortByOption.dir &&
            option.property === selectedSortByOption.property
          )
      )
      return data
    }
    return []
  }

  return (
    <Overlay
      show={show}
      placement="bottom-end"
      popperConfig={{ strategy: 'absolute' }}
      target={parentRef.current}
    >
      {({ ...props }) => (
        <OuterWrapper
          {...props}
          style={{
            ...props.style,
            zIndex: zIndex.dropdown,
          }}
        >
          <Box>
            <Grow in={show}>
              <Wrapper ref={overlayRef}>
                <FieldWrapper>
                  <Label>
                    <I18n name="sortBy" />
                  </Label>
                  <ReactSelectWrapper>
                    {selectedSortByOption ? (
                      <ReactSelect
                        defaultValue={selectedSortByOption}
                        onChange={onChangeSortBy}
                        options={getSortByOptions()}
                        getOptionLabel={(option: unknown) =>
                          (option as Option).label
                        }
                        getOptionValue={(option: unknown) =>
                          (option as Option).label
                        }
                      />
                    ) : null}
                  </ReactSelectWrapper>
                </FieldWrapper>
                <FieldWrapper>
                  <Label>
                    <I18n name="thenBy" />
                  </Label>
                  <ReactSelectWrapper>
                    {selectedThenByOption ? (
                      <ReactSelect
                        defaultValue={selectedThenByOption}
                        onChange={onChangeThenBy}
                        options={getThenByOptions()}
                        getOptionLabel={(option: unknown) =>
                          (option as Option).label
                        }
                        getOptionValue={(option: unknown) =>
                          (option as Option).label
                        }
                      />
                    ) : null}
                  </ReactSelectWrapper>
                </FieldWrapper>
              </Wrapper>
            </Grow>
          </Box>
        </OuterWrapper>
      )}
    </Overlay>
  )
}

const OuterWrapper = styled.div`
  display: inline-block;
`

const Wrapper = styled.div`
  position: absolute;
  width: 348px;
  right: 0;
  background: #ffffff;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
`

const FieldWrapper = styled.div`
  display: flex;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 10px;
  border-bottom: 1px solid #d3d2cf;
  align-items: center;
`
const Label = styled.span`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 24px;
  color: #050505;
  margin-right: 5px;
  width: 60px;
`
const ReactSelectWrapper = styled.div`
  width: 240px;
`
